import { GEE_DEFAULT_BANDS } from '@/config/layers';

import type { AlphaEarthIndex } from '../types/map.types';

/** Chuỗi `bands` mặc định cho RGB: ba kênh embedding (`GEE_DEFAULT_BANDS`) gán R–G–B hiển thị. */
const RGB_EMBEDDING_BANDS = GEE_DEFAULT_BANDS.join(',');

/**
 * Ba màu minh họa blog DeepMind AlphaEarth Foundations (gán R–G–L cho ba chiều trong trường embedding).
 * Định dạng CSV cho query `palette` trên BE (thử nghiệm tô màu).
 * @see https://deepmind.google/blog/alphaearth-foundations-helps-map-our-planet-in-unprecedented-detail/
 */
export const ALPHAEARTH_DEEPMIND_EMBEDDING_PALETTE = 'red,green,blue' as const;

/**
 * Chuỗi `palette` gửi lên API: ưu tiên ghi đè từ Redux/UI; không thì mặc định bảng DeepMind (hoặc gợi ý BE cho BAIS2).
 */
export function resolveAlphaEarthPaletteForRequest(
  index: AlphaEarthIndex,
  paletteOverride: string | null | undefined,
): string | null {
  if (paletteOverride != null && String(paletteOverride).trim() !== '') {
    return String(paletteOverride).trim();
  }
  if (index === 'BAIS2') {
    return 'green,yellow,red';
  }
  return ALPHAEARTH_DEEPMIND_EMBEDDING_PALETTE;
}

/**
 * Trả về chuỗi `bands` cho query (vd. `B3,B8`, RGB mặc định `A01,A16,A09`) — ưu tiên override từ UI, không thì default theo `index` (khớp BE).
 * `BAIS2`: BE bỏ qua `bands` — trả `null` để không thêm query param.
 */
/**
 * Palette mặc định cho chỉ số phổ Sentinel-2 (F-AI-02).
 * RGB true-color (B4,B3,B2) không gửi `palette`.
 */
export function resolveSpectralIndexPaletteForSarPreview(index: AlphaEarthIndex): string | null {
  switch (index) {
    case 'NDWI':
    case 'MNDWI':
      return 'white,blue';
    case 'NDVI':
      return 'brown,yellow,green';
    case 'NDBI':
      return 'white,red';
    case 'BAIS2':
      return 'green,yellow,red';
    case 'RGB':
    default:
      return null;
  }
}

export function resolveAlphaEarthBandsParam(
  index: AlphaEarthIndex,
  spectralBands: string | null | undefined,
): string | null {
  if (spectralBands != null && String(spectralBands).trim()) {
    return String(spectralBands).trim();
  }
  switch (index) {
    case 'NDWI':
      return 'B3,B8';
    case 'MNDWI':
      return 'B3,B11';
    case 'NDVI':
      return 'B8,B4';
    case 'NDBI':
      return 'B11,B8';
    case 'BAIS2':
      return null;
    case 'RGB':
    default:
      return RGB_EMBEDDING_BANDS;
  }
}

/** Tint gợi ý cho BitmapLayer khi chỉ số đã được tô trên server (minh họa nhẹ). */
export function alphaEarthBitmapTintColor(
  index: AlphaEarthIndex,
): [number, number, number, number] {
  switch (index) {
    case 'NDWI':
      return [60, 140, 220, 255];
    case 'MNDWI':
      return [60, 160, 230, 255];
    case 'NDVI':
      return [60, 200, 80, 255];
    case 'NDBI':
      return [220, 80, 60, 255];
    case 'BAIS2':
      return [200, 120, 40, 255];
    case 'RGB':
    default:
      return [255, 255, 255, 255];
  }
}
